import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store';
import { logout as logoutAction, setCredentials, setCurrentWorkspace } from '../store/slices/authSlice';
import { logout as logoutApi } from '../utils/auth';
import axiosInstance from '../api/axios';

/**
 * Authentication context and hooks
 * Provides authentication state and methods throughout the app
 * Requirements: 1.1, 1.7
 */

interface AuthContextType {
  isAuthenticated: boolean;
  user: any | null;
  token: string | null;
  currentWorkspace: any | null;
  workspaces: any[];
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state: RootState) => state.auth);

  // Handle token refresh
  const refreshToken = async () => {
    try {
      const response = await axiosInstance.post('/auth/refresh', {
        refreshToken: auth.token,
      });

      if (response.data.token) {
        dispatch(
          setCredentials({
            user: auth.user!,
            token: response.data.token,
            workspaces: auth.workspaces,
          })
        );
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If refresh fails, logout user
      await logout();
    }
  };

  // Handle logout
  const logout = async () => {
    try {
      await logoutApi();
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      dispatch(logoutAction());
      navigate('/login');
    }
  };

  // Check token expiration and refresh if needed
  useEffect(() => {
    if (!auth.token) return;

    // Decode JWT to check expiration
    try {
      const tokenParts = auth.token.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        const expirationTime = payload.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        const timeUntilExpiration = expirationTime - currentTime;

        // Refresh token 5 minutes before expiration
        const refreshTime = timeUntilExpiration - 5 * 60 * 1000;

        if (refreshTime > 0) {
          const refreshTimer = setTimeout(() => {
            refreshToken();
          }, refreshTime);

          return () => clearTimeout(refreshTimer);
        } else {
          // Token already expired or about to expire
          logout();
        }
      }
    } catch (error) {
      console.error('Error parsing token:', error);
    }

    return undefined;
  }, [auth.token]);

  const value: AuthContextType = {
    isAuthenticated: auth.isAuthenticated,
    user: auth.user,
    token: auth.token,
    currentWorkspace: auth.currentWorkspace,
    workspaces: auth.workspaces,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access authentication context
 * @returns Authentication context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
